import { formatNumber } from 'cesg-shared';
import type { SessionUser } from 'cesg-shared/dist/models';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLiftPlanPermissions } from '../permissions/useLiftPlanPermissions';
import type {
  LiftTableComponentElementState,
  LiftTableComponentState,
  LiftTableInputState,
} from '../sub-components/lift-table/LiftTable.vue';
import type {
  LiftPlanEntry,
  LiftPlanEntryColumnKey,
  LiftPlanSubtotalRow,
} from './createLiftPlanStore';
import {
  ALL_COLUMN_KEYS,
  createLiftPlanStore,
  type LiftPlan,
  type SaveLiftPlanData,
} from './createLiftPlanStore';

export type { SaveLiftPlanData };

// --- Internal types ---

type LiftPlanInputState = 'edit' | 'read-only' | 'hidden';

type LiftPlanSubtotalColumnKey =
  | 'label'
  | 'amount'
  | 'invoicedDate'
  | 'paymentDate'
  | 'comment';

interface LiftPlanColumn {
  key: LiftPlanEntryColumnKey;
  label: string;
  state: LiftPlanInputState;
  getValue: (entry: LiftPlanEntry) => string;
}

interface LiftPlanSubtotalColumn {
  key: LiftPlanSubtotalColumnKey;
  state: LiftPlanInputState;
  getValue: (row: LiftPlanSubtotalRow) => string;
}

// --- Component state ---

export type LiftPlanComponentElementState = LiftTableComponentElementState;
export type LiftPlanComponentInputState = LiftTableInputState;
export type LiftPlanComponentState = {
  liftTable: LiftTableComponentState;
  sectionCard: {
    edit: LiftPlanComponentElementState;
    title: LiftPlanComponentInputState;
    delete?: LiftPlanComponentElementState;
    isSaving?: boolean;
  };
  contractSum: LiftPlanComponentInputState;
  retentionPercentage: LiftPlanComponentInputState;
  retentionAmount: LiftPlanComponentInputState;
  entreprenadsumma: LiftPlanComponentInputState;
};

// --- Props ---

type LiftPlanEmit = (event: 'save', body: SaveLiftPlanData) => void;

export interface UseLiftPlanLogicProps {
  initialLiftPlan: Omit<
    LiftPlan,
    'totalAmount' | 'totalRetention' | 'totalAmountDue' | 'entreprenadsumma'
  >;
  user: SessionUser;
  projectId: string;
  isSaving?: boolean;
}

// --- Column metadata ---

const GET_VALUE: Record<
  LiftPlanEntryColumnKey,
  (entry: LiftPlanEntry) => string
> = {
  number: (entry) => entry.number.toString(),
  description: (entry) => entry.description ?? '',
  dueDate: (entry) =>
    entry.dueDate ? entry.dueDate.toISOString().slice(0, 10) : '',
  amount: (entry) => formatNumber(entry.amount),
  retention: (entry) => formatNumber(entry.retention),
  amountDue: (entry) => formatNumber(entry.amountDue),
  invoicedDate: (entry) =>
    entry.invoicedDate ? entry.invoicedDate.toISOString().slice(0, 10) : '',
  paymentDate: (entry) =>
    entry.paymentDate ? entry.paymentDate.toISOString().slice(0, 10) : '',
  comment: (entry) => entry.comment ?? '',
};

const SUBTOTAL_GET_VALUE: Record<
  LiftPlanSubtotalColumnKey,
  (row: LiftPlanSubtotalRow) => string
> = {
  label: (row) => row.label,
  amount: (row) => formatNumber(row.amount),
  invoicedDate: (row) =>
    row.invoicedDate ? row.invoicedDate.toISOString().slice(0, 10) : '',
  paymentDate: (row) =>
    row.paymentDate ? row.paymentDate.toISOString().slice(0, 10) : '',
  comment: (row) => row.comment ?? '',
};

// --- Composable ---

type LiftPlanMode = 'normal' | 'edit' | 'saving';

export const useLiftPlanLogic = (
  props: UseLiftPlanLogicProps,
  emit: LiftPlanEmit,
) => {
  const { initialLiftPlan, user, projectId } = props;
  const { t } = useI18n();

  const { state, dispatch } = createLiftPlanStore(initialLiftPlan);

  const mode = ref<LiftPlanMode>('normal');

  watch(
    () => props.isSaving,
    (saving) => {
      if (!saving && mode.value === 'saving') {
        mode.value = 'normal';
      }
    },
  );

  const permissions = useLiftPlanPermissions({
    isEditing: () => state.value.isEditing,
    user,
    projectId,
  });

  // Computed state

  const columns = computed<
    Partial<Record<LiftPlanEntryColumnKey, LiftPlanColumn>>
  >(() => {
    const result: Partial<Record<LiftPlanEntryColumnKey, LiftPlanColumn>> = {};
    for (const key of ALL_COLUMN_KEYS) {
      if (state.value.columnStates[key] !== 'hidden') {
        result[key] = {
          key,
          label: t(`projects.liftPlan.columns.${key}`),
          state: permissions.value.columns[key],
          getValue: GET_VALUE[key],
        };
      }
    }
    return result;
  });

  const subtotalColumns = computed<
    Partial<Record<LiftPlanSubtotalColumnKey, LiftPlanSubtotalColumn>>
  >(() => ({
    label: {
      key: 'label',
      state: permissions.value.subtotalColumns.label,
      getValue: SUBTOTAL_GET_VALUE.label,
    },
    amount: {
      key: 'amount',
      state: permissions.value.subtotalColumns.amount,
      getValue: SUBTOTAL_GET_VALUE.amount,
    },
    invoicedDate: {
      key: 'invoicedDate',
      state: permissions.value.subtotalColumns.invoicedDate,
      getValue: SUBTOTAL_GET_VALUE.invoicedDate,
    },
    paymentDate: {
      key: 'paymentDate',
      state: permissions.value.subtotalColumns.paymentDate,
      getValue: SUBTOTAL_GET_VALUE.paymentDate,
    },
    comment: {
      key: 'comment',
      state: permissions.value.subtotalColumns.comment,
      getValue: SUBTOTAL_GET_VALUE.comment,
    },
  }));

  const componentState = computed(
    () =>
      ({
        ...permissions.value.components,
        liftTable: {
          ...permissions.value.components,
          ...permissions.value.columns,
        },
        sectionCard: {
          ...permissions.value.components.sectionCard,
          isSaving: mode.value === 'saving',
          save: state.value.isDirty ? 'visible' : 'disabled',
        },
      }) as LiftPlanComponentState,
  );

  // Public action interface

  const action = {
    toggleEdit: () => {
      if (mode.value === 'normal') {
        dispatch({ type: 'toggle-edit' });
        mode.value = 'edit';
      } else if (mode.value === 'edit') {
        dispatch({ type: 'toggle-edit' });
        mode.value = 'saving';
        emit('save', {
          id: state.value.liftPlan.id,
          name: state.value.liftPlan.name,
          contractSum: state.value.liftPlan.contractSum,
          retentionPercentage: state.value.liftPlan.retentionPercentage,
          entries: state.value.liftPlan.entries.map((e) => ({ ...e })),
          subtotalRows: state.value.liftPlan.subtotalRows.map((r) => ({
            ...r,
          })),
        });
      }
    },
    cancel: () => {
      dispatch({ type: 'cancel' });
      mode.value = 'normal';
    },
    delete: () => {
      dispatch({ type: 'delete' });
    },
    updateName: (event: { value: string }) => {
      dispatch({ type: 'update-name', value: event.value });
    },
    updateRetentionPercentage: (event: { value: string }) => {
      dispatch({
        type: 'update-plan',
        field: 'retentionPercentage',
        value: event.value,
      });
    },
    addEntry: () => {
      dispatch({ type: 'add-entry' });
    },
    deleteEntry: (event: { id: string }) => {
      dispatch({ type: 'delete-entry', id: event.id });
    },
    updateEntry: (event: {
      id: string;
      field: LiftPlanEntryColumnKey;
      value: string;
    }) => {
      dispatch({ type: 'update-entry', ...event });
    },
    addSubtotal: () => {
      dispatch({ type: 'add-subtotal' });
    },
    deleteSubtotal: (event: { id: string }) => {
      dispatch({ type: 'delete-subtotal', id: event.id });
    },
    updateSubtotal: (event: {
      id: string;
      field: 'label' | 'amount' | 'invoicedDate' | 'paymentDate' | 'comment';
      value: string;
    }) => {
      dispatch({ type: 'update-subtotal', ...event });
    },
    setColumnState: (event: {
      key: LiftPlanEntryColumnKey;
      state: 'visible' | 'hidden';
    }) => {
      dispatch({
        type: 'set-column-state',
        key: event.key,
        state: event.state,
      });
    },
  };

  const computedState = computed(() => ({
    componentState: componentState.value,
    columns: columns.value,
    subtotalColumns: subtotalColumns.value,
    liftPlan: state.value.liftPlan,
    isEditing: state.value.isEditing,
  }));

  return { state: computedState, action };
};
